import { isPastIsoDate } from '@upms/dates';
import { Payment, PaymentMethod, PaymentMethodType, PaymentPlan } from './types';

// ---------------------------------------------------------------------------
// Migration boundary
//
// The billing SDK exposes raw field values but does not derive higher-level
// states from them — whether a plan is paid in full, whether a payment is a
// refund, whether a policy has a payment method on file. Those gaps are
// currently filled client-side here.
//
// All such derivations live in this file, adjacent to the SDK contract, so
// that when the billing backend team exposes equivalent logic the migration is
// a single deletion point: remove the predicate, update the import, done. Do
// not scatter these checks across components or hooks.
// ---------------------------------------------------------------------------

// --- PaymentPlan predicates -------------------------------------------------

// `null` (SDK 0.0.44+) returns false — treat unknown remaining premium as not paid in full.
export const isPaidInFull = ({
  totalRemainingPremium,
}: Pick<PaymentPlan, 'totalRemainingPremium'>) => totalRemainingPremium === 0;

// A payment is due when nextPaymentAmount is a positive number with a due date.
// Two API states mean "nothing due": null (credit balance, per BILL-734) and
// zero with no due date (annual policy between billing cycles).
// Use !hasPaymentDue to show "Paid in full".
export const hasPaymentDue = (
  plan: Pick<PaymentPlan, 'nextPaymentAmount' | 'nextPaymentDueDate'>
): plan is Pick<PaymentPlan, 'nextPaymentAmount' | 'nextPaymentDueDate'> & {
  nextPaymentAmount: number;
} =>
  plan.nextPaymentAmount !== null &&
  !(plan.nextPaymentAmount === 0 && plan.nextPaymentDueDate === null);

// An outstanding balance exists whenever the aggregate `balance` is positive
// (SDK 0.0.47+ always returns the field; zero means nothing is owed).
export const hasOutstandingBalance = ({ balance }: Pick<PaymentPlan, 'balance'>): boolean =>
  balance > 0;

// Past due only when the earliest open installment date is strictly before today —
// on the day of invoicing (openBalanceSince === today) the balance shows but no
// past-due status. Uses local-timezone comparison so US evening reads don't flip
// early. `openBalanceSince` is null whenever no invoice is open.
export const isPastDue = ({
  balance,
  openBalanceSince,
}: Pick<PaymentPlan, 'balance' | 'openBalanceSince'>): boolean => {
  if (balance <= 0) return false;
  if (!openBalanceSince) return false;
  return isPastIsoDate(openBalanceSince);
};

// Payroll plans are managed by the employer's payroll partner, not the insured
// directly — they have a different UI and no next-payment display.
export const isPayrollPaymentPlan = ({ paymentPlan }: Pick<PaymentPlan, 'paymentPlan'>) =>
  paymentPlan === 'Payroll';

// Billing exposes no credit field, so a negative remaining premium is the only
// signal that money is owed back to the policyholder (overpayment, or premium
// adjusted down — e.g. proration on cancellation).
export const hasCredit = ({ totalRemainingPremium }: Pick<PaymentPlan, 'totalRemainingPremium'>) =>
  (totalRemainingPremium ?? 0) < 0;

// --- Payment predicates -----------------------------------------------------

// A payment is "associated" when it's linked to a specific policy the insured
// can navigate to. Unassociated payments (e.g. account-level refunds) have a
// null policyId.
export const isAssociatedPayment = (payment: Payment): payment is Payment & { policyId: string } =>
  payment.policyId !== null;

// A payment is a refund when its amount is negative. No type predicate here:
// `Payment` has no discriminant on `amount`, so the type cannot be narrowed.
export const isRefund = (payment: Payment): boolean => payment.amount < 0;

// --- PaymentMethod (row) accessors ------------------------------------------

// A row's "primary" policy is the latest term — the entry the row link,
// entity-name lookup, and edit destination all target. The mapper sorts
// `policies` newest-first (policyNumber desc), so index 0 is authoritative.
// Extracted so the "index 0 = latest" invariant lives in one place.
export const getPrimaryPolicy = (paymentMethod: PaymentMethod) => paymentMethod.policies[0];

// --- PaymentMethodType predicates -------------------------------------------

type MissingPaymentMethod = Extract<PaymentMethodType, { kind: 'missing' }>;
type UnknownPaymentMethod = Extract<PaymentMethodType, { kind: 'unknown' }>;

// A payment method exists and can be displayed or edited.
// Use !hasPaymentMethod to check for the missing case.
export const hasPaymentMethod = (
  method: PaymentMethodType
): method is Exclude<PaymentMethodType, MissingPaymentMethod> => method.kind !== 'missing';

// A payment method is "unknown" when one exists but its type cannot be identified.
// Renders a generic card icon instead of a brand-specific one.
export const isUnknownPaymentMethod = (method: PaymentMethodType): method is UnknownPaymentMethod =>
  method.kind === 'unknown';

// --- Card expiration predicates ---------------------------------------------

type CardPaymentMethod = Extract<PaymentMethodType, { kind: 'card' }>;

// Returns true when the card's expiration month has passed.
// expirationMonth is 1-indexed (1 = January, 12 = December); a card expiring
// in month M of year Y becomes invalid on the first of month M+1, which
// coincidentally equals new Date(Y, M, 1) in JS's 0-indexed month system.
export const isCardExpired = ({ expirationMonth, expirationYear }: CardPaymentMethod): boolean => {
  if (expirationMonth == null || expirationYear == null) return false;
  return Date.now() >= new Date(expirationYear, expirationMonth, 1).getTime();
};

// Returns true when the card expires within 30 days.
export const isCardExpiringSoon = ({
  expirationMonth,
  expirationYear,
}: CardPaymentMethod): boolean => {
  if (expirationMonth == null || expirationYear == null) return false;
  const msUntilExpiration = new Date(expirationYear, expirationMonth, 1).getTime() - Date.now();
  return msUntilExpiration > 0 && msUntilExpiration < 30 * 24 * 60 * 60 * 1000;
};
